/** Flow 5 — Accompagnement à la tâche (simulation, no backend) */

export const FLOW5_TITLE = "Flow 5 — Accompagnement à la tâche";

export const FLOW5_STEPS = [
  "Rappel",
  "Démarrage",
  "Suivi",
  "Difficulté",
  "Envoi au tuteur",
];

export const FLOW5_TASKS = [
  { id: "f5-task-maths-fonctions", label: "Maths – Fonctions" },
  { id: "f5-task-francais", label: "Français – Dissertation" },
  { id: "f5-task-sciences", label: "Sciences – Chapitre 3" },
  { id: "f5-task-histoire", label: "Histoire – Frise chrono" },
  { id: "f5-task-exercices", label: "Maths – Exercices" },
];

export const FLOW5_RATINGS = [
  { id: "5", label: "5 · facile" },
  { id: "4", label: "4 · plutôt facile" },
  { id: "3", label: "3 · moyen" },
  { id: "2", label: "2 · plutôt difficile" },
  { id: "1", label: "1 · difficile" },
  { id: "0", label: "0 · très difficile" },
];

export const FLOW5_CANON = {
  task: "Maths – Fonctions",
  started: "18 h 00",
  ended: "18 h 45",
  duration: "45 min",
  pause: "Aucune",
  pausedAt: "18 h 20",
  resumedAt: "18 h 30",
  endedAfterPause: "18 h 55",
  pauseLength: "10 min",
  rating: "5 · facile",
  note: "La question 3 était trop dure.",
};

export const REMINDER_MESSAGE =
  "📌 Petit rappel : tu t'étais engagé(e) à étudier Maths – Fonctions pendant 30 minutes, entre 17 h et 18 h aujourd'hui.";

export const ASK_START_MESSAGE = "Veux-tu commencer maintenant ?";

export const TASK_MESSAGE = REMINDER_MESSAGE;

export function beginMessage(task) {
  return `C'est noté : ${task || FLOW5_CANON.task}. Dis-moi quand tu démarres.`;
}

export const STARTED_MESSAGE = "C'est parti à 18 h 00. Je reste avec toi.";

export const CHECK_MESSAGE = "Tout va bien ?";

export const OK_MESSAGE = "Parfait, continue comme ça.";

export const AGAIN_MESSAGE = "Toujours tout va bien ?";

export const OK_AGAIN_MESSAGE = "Super, tu avances bien.";

export const NOTED_MESSAGE = "C'est noté. On continue ?";

export const COLLECT_MESSAGE =
  "Je rassemble les infos de ta séance : la tâche, le temps calculé et la difficulté.";

export const PAUSE_MESSAGE = "Pause à 18 h 20. Le temps est arrêté.";

export const RESUME_MESSAGE = "Reprise à 18 h 30. Le temps repart.";

export function timingFor(hadPause, inProgress = false) {
  if (inProgress) {
    return {
      started: FLOW5_CANON.started,
      ended: "En cours",
      pause: hadPause ? FLOW5_CANON.pauseLength : FLOW5_CANON.pause,
      duration: hadPause ? "20 min" : "25 min",
    };
  }
  if (hadPause) {
    return {
      started: FLOW5_CANON.started,
      ended: FLOW5_CANON.endedAfterPause,
      pause: FLOW5_CANON.pauseLength,
      duration: FLOW5_CANON.duration,
    };
  }
  return {
    started: FLOW5_CANON.started,
    ended: FLOW5_CANON.ended,
    pause: FLOW5_CANON.pause,
    duration: FLOW5_CANON.duration,
  };
}

export function doneMessage(hadPause) {
  const timing = timingFor(hadPause);
  if (hadPause) {
    return `Tu as fini à ${timing.ended}. J'ai retiré ${timing.pause} de pause. Temps calculé : ${timing.duration}.`;
  }
  return `Tu as fini à ${timing.ended}. Temps calculé : ${timing.duration}.`;
}

export const RATE_MESSAGE =
  "Comment s’est passée cette séance d’étude ? 5, c’était facile. 1 ou 0, c’était difficile. S’il y a eu une difficulté, tu peux la noter.";

export const NOTE_MESSAGE = "Note la difficulté en une phrase.";

export const SEND_MESSAGE =
  "Je prépare l’envoi à ton tuteur : la tâche, le temps calculé, et comment s’est passée la séance.";

export const SENT_MESSAGE = "C’est envoyé à ton tuteur.";

export const SETTLED_MESSAGE =
  "Ces infos sont déjà parties. Choisis une étape dans le sommaire, ou appuie sur Rejouer.";

export function flow5DisplayIndex(flowStep) {
  if (flowStep <= 0) return 0;
  if (flowStep === 1) return 1;
  if (flowStep === 2) return 2;
  if (flowStep === 3 || flowStep === 4) return 3;
  return 4;
}

export function tutorRows(session = {}) {
  const timing = timingFor(Boolean(session.hadPause));
  return [
    ["Tâche", session.task || FLOW5_CANON.task],
    ["Début", session.started || timing.started],
    ["Fin", session.ended || timing.ended],
    ["Pause", session.pause || timing.pause],
    ["Temps calculé", session.duration || timing.duration],
    [
      "Séance",
      session.note
        ? `Difficulté : ${session.note}`
        : session.rating || "Pas encore notée",
    ],
  ];
}

const taskReplies = FLOW5_TASKS.map((task) => ({
  id: task.id,
  label: task.label,
  primary: task.label === FLOW5_CANON.task,
}));

export const FLOW5_REPLIES = {
  task: taskReplies,
  startNow: [{ id: "f5-start-now", label: "Oui je commence !", primary: true }],
  begin: [{ id: "f5-begin", label: "Je commence", primary: true }],
  check: [
    { id: "f5-ok", label: "Tout va bien", primary: true },
    { id: "f5-pause", label: "Pause", primary: false },
    { id: "f5-end", label: "J'ai terminé", primary: false },
    { id: "f5-diff", label: "Difficulté", primary: false },
  ],
  finish: [
    { id: "f5-end", label: "J'ai terminé", primary: true },
    { id: "f5-pause", label: "Pause", primary: false },
    { id: "f5-diff", label: "Difficulté", primary: false },
  ],
  resume: [{ id: "f5-resume", label: "Je reprends", primary: true }],
  rate: [
    ...FLOW5_RATINGS.map((item) => ({
      id: `f5-star-${item.id}`,
      label: item.label,
      primary: item.id === "5",
    })),
    { id: "f5-note", label: "Noter une difficulté", primary: false },
  ],
  note: [{ id: "f5-note-sample", label: FLOW5_CANON.note, primary: true }],
  send: [{ id: "f5-send", label: "Continuer", primary: true }],
};

function result(messages, quickReplies, flowStep, extra = {}) {
  const timing = timingFor(Boolean(extra.hadPause));
  return {
    messages,
    quickReplies,
    flowStep,
    status: FLOW5_STEPS[flow5DisplayIndex(flowStep)],
    planReady: flowStep >= 5,
    session: {
      task: extra.task || "",
      duration: extra.duration || "",
      difficulty: extra.rating || "",
      blockage: extra.note || "",
      hadPause: Boolean(extra.hadPause),
      started: extra.started || "",
      ended: extra.ended || "",
      pause: extra.pause || "",
      checks: extra.checks || 0,
    },
    timing,
  };
}

/** Instant history for a sommaire step (display index 0–4). */
export function buildFlow5Snapshot(displayIndex) {
  const step = Math.min(Math.max(displayIndex, 0), FLOW5_STEPS.length - 1);
  const task = FLOW5_CANON.task;
  const messages = [
    { sender: "agent", messageKey: "f5.remind", text: REMINDER_MESSAGE },
    { sender: "agent", messageKey: "f5.askStart", text: ASK_START_MESSAGE },
  ];
  if (step === 0) return result(messages, FLOW5_REPLIES.startNow, 0);

  messages.push({ sender: "user", text: "Oui je commence !" });
  messages.push({ sender: "agent", messageKey: "f5.started", text: STARTED_MESSAGE });
  if (step === 1) return result(messages, [], 1, { task, started: FLOW5_CANON.started });

  messages.push({ sender: "agent", messageKey: "f5.check", text: CHECK_MESSAGE });
  if (step === 2) {
    return result(messages, FLOW5_REPLIES.check, 2, { task, started: FLOW5_CANON.started });
  }

  messages.push({ sender: "user", text: "Tout va bien" });
  messages.push({ sender: "agent", messageKey: "f5.ok", text: OK_MESSAGE });
  messages.push({ sender: "agent", messageKey: "f5.again", text: AGAIN_MESSAGE });
  messages.push({ sender: "user", text: "J'ai terminé" });
  messages.push({ sender: "agent", messageKey: "f5.done", text: doneMessage(false) });
  messages.push({ sender: "agent", messageKey: "f5.rate", text: RATE_MESSAGE });
  if (step === 3) {
    return result(messages, FLOW5_REPLIES.rate, 3, {
      task,
      started: FLOW5_CANON.started,
      ended: FLOW5_CANON.ended,
      pause: FLOW5_CANON.pause,
      duration: FLOW5_CANON.duration,
    });
  }

  const timing = timingFor(false);
  messages.push({ sender: "user", text: FLOW5_CANON.rating });
  messages.push({ sender: "agent", messageKey: "f5.send", text: SEND_MESSAGE });
  messages.push({
    sender: "agent",
    kind: "tutorNote",
    rows: tutorRows({
      task,
      ...timing,
      rating: FLOW5_CANON.rating,
    }),
  });
  messages.push({ sender: "user", text: "Continuer" });
  messages.push({ sender: "agent", messageKey: "f5.sent", text: SENT_MESSAGE });
  return result(messages, [], 6, {
    task,
    ...timing,
    rating: FLOW5_CANON.rating,
  });
}
